import { COMPANY_TZ } from "@/lib/constants";
import {
  AUTODIALER_ACTIVE_QUEUE_LOOKBACK_DAYS,
  AUTODIALER_QUEUE_DAY_START_HOUR_ET,
  SCHEDULED_CALL_END_HOUR,
  SCHEDULED_CALL_PROMPT_WINDOW_MINUTES,
  SCHEDULED_CALL_START_HOUR,
  WORKFLOW_INACTIVITY_TIMEOUT_MS,
} from "@/lib/modules/calls/workflow/constants";
import type {
  CallWorkflowQueueItem,
  CallWorkflowState,
  WorkflowRedisSession,
  WorkflowSessionSummaryStats,
} from "@/lib/modules/calls/workflow/types";
import {
  DEFAULT_TIMEZONE,
  type SupportedTimezone,
} from "@marble/core/modules/timezones";
import { DateTime } from "luxon";

export const WORKFLOW_QUEUE_POLL_INTERVAL_MS = 1_800_000;
export const WORKFLOW_SESSION_SYNC_INTERVAL_MS = 30_000;
/** Re-prefetch the next queue student's profile. No-op while the client router cache is still fresh (~5 min). */
export const WORKFLOW_NEXT_PROFILE_PREFETCH_INTERVAL_MS = 180_000;
/** Stop interval-prefetching a given next student after this many attempts, including the first. */
export const WORKFLOW_NEXT_PROFILE_PREFETCH_MAX_ATTEMPTS = 5;
/**
 * How long to wait before recovering a stuck "Opening {name}…" profile
 * overlay. Slow `getStudent` layouts can take a few seconds; overlapping
 * `router.push` calls can cancel the RSC entirely and never resolve.
 */
export const WORKFLOW_PROFILE_TRANSITION_RETRY_MS = 8_000;
/** Soft-nav retries before giving up on a stuck next-student overlay. */
export const WORKFLOW_PROFILE_TRANSITION_MAX_RETRIES = 3;

const WORKFLOW_CALL_ACTIVE_STATES: CallWorkflowState[] = [
  "CONNECTING",
  "ACTIVE_CALL",
  "POST_CALL_LOGGING",
];

export const isWorkflowCallInProgress = (state: CallWorkflowState): boolean =>
  WORKFLOW_CALL_ACTIVE_STATES.includes(state);

const INACTIVITY_PROTECTED_STATES: CallWorkflowState[] = [
  "CONNECTING",
  "ACTIVE_CALL",
  "POST_CALL_LOGGING",
];

/**
 * True when a persisted session should be discarded:
 * - active call/log states never expire
 * - modern sessions expire from their latest activity anchor
 * - legacy sessions fall back to startedAt instead of being invalidated only
 *   because they predate lastCallStartedAt
 */
export const isWorkflowSessionExpiredByInactivity = (
  session: WorkflowRedisSession,
  nowMs: number = Date.now(),
): boolean => {
  if (INACTIVITY_PROTECTED_STATES.includes(session.state)) {
    return false;
  }

  const activityAtMs = Date.parse(
    session.lastCallStartedAt ?? session.startedAt,
  );
  if (Number.isNaN(activityAtMs)) {
    return false;
  }

  return nowMs - activityAtMs > WORKFLOW_INACTIVITY_TIMEOUT_MS;
};

/** User-facing task phase in the autodialer. Maps the workflow state machine. */
export type WorkflowTaskPhase = "prep" | "call";

export const getWorkflowTaskPhase = (
  state: CallWorkflowState,
): WorkflowTaskPhase => {
  if (
    state === "CONNECTING" ||
    state === "ACTIVE_CALL" ||
    state === "POST_CALL_LOGGING"
  ) {
    return "call";
  }
  return "prep";
};

/** VoIP session signals used when stopping a workflow mid-call. */
export type WorkflowStopVoiceCallSession = {
  callSid: string | null;
  isCallActive: boolean;
  isOutboundRinging: boolean;
  status: string;
};

/**
 * True when stopping the workflow should tear down a live or in-flight outbound
 * call — including Twilio connect/ringing before the workflow reaches ACTIVE_CALL.
 */
export const shouldEndVoiceCallOnWorkflowStop = (
  workflowState: CallWorkflowState,
  session: WorkflowStopVoiceCallSession,
): boolean => {
  if (isWorkflowCallInProgress(workflowState)) {
    return true;
  }

  if (session.callSid) {
    return true;
  }

  return (
    session.isCallActive ||
    session.isOutboundRinging ||
    session.status === "connecting" ||
    session.status === "in_call"
  );
};

/** Matches Tailwind `md` — workflow sidebar and controls are desktop-only. */
const WORKFLOW_MIN_VIEWPORT_PX = 768;

export const isCompactWorkflowViewport = (): boolean => {
  if (typeof window === "undefined") {
    return false;
  }

  return window.innerWidth < WORKFLOW_MIN_VIEWPORT_PX;
};

export const generateSessionId = (): string => {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
};

/** Current autodialer queue day: most recent 7:00 AM ET through the next 7:00 AM ET. */
export const getAutodialerQueueDayBounds = (
  now: Date = new Date(),
): { start: Date; end: Date } => {
  const zonedNow = DateTime.fromJSDate(now, { zone: COMPANY_TZ });
  let start = zonedNow.set({
    hour: AUTODIALER_QUEUE_DAY_START_HOUR_ET,
    minute: 0,
    second: 0,
    millisecond: 0,
  });
  if (zonedNow < start) {
    start = start.minus({ days: 1 });
  }

  return {
    start: start.toUTC().toJSDate(),
    end: start.plus({ days: 1 }).toUTC().toJSDate(),
  };
};

export const getWorkflowDateBoundaries = () => {
  const { start, end } = getAutodialerQueueDayBounds();

  return {
    queueWindowStartUtc: start,
    queueWindowEndUtc: end,
  };
};

/** Milliseconds until the current 7am ET queue day rolls over. */
export const getWorkflowQueueBoundaryRefreshDelayMs = (
  now: Date = new Date(),
): number => {
  const { end } = getAutodialerQueueDayBounds(now);
  return Math.max(0, end.getTime() - now.getTime());
};

const dueAtInTimezone = (dueAt: Date, timezone: SupportedTimezone): DateTime =>
  DateTime.fromJSDate(dueAt).setZone(timezone);

/** True when dueAt is 7:00 AM–9:00 PM in the given timezone.
 *  Legacy time-of-day heuristic; autodialer routing uses `isScheduled`. */
export const isScheduledCall = (
  dueAt: Date | null,
  timezone: SupportedTimezone = DEFAULT_TIMEZONE,
): boolean => {
  if (dueAt == null) {
    return false;
  }

  const zoned = dueAtInTimezone(dueAt, timezone);
  const minutes = zoned.hour * 60 + zoned.minute;
  return (
    minutes >= SCHEDULED_CALL_START_HOUR * 60 &&
    minutes <= SCHEDULED_CALL_END_HOUR * 60
  );
};

const isDueAtInQueueDay = (dueAt: Date, now: Date): boolean => {
  const { start, end } = getAutodialerQueueDayBounds(now);
  const time = dueAt.getTime();
  return time >= start.getTime() && time < end.getTime();
};

const promptWindowMs = SCHEDULED_CALL_PROMPT_WINDOW_MINUTES * 60 * 1000;

/**
 * Held on the scheduled stack: flagged `isScheduled`, due in the current
 * 7am–7am ET queue day, and not yet past dueAt + 10 minutes.
 */
export const isHeldScheduledCall = (
  dueAt: Date | null,
  now: Date = new Date(),
  isScheduled: boolean = false,
): boolean => {
  if (!isScheduled || dueAt == null) {
    return false;
  }
  if (!isDueAtInQueueDay(dueAt, now)) {
    return false;
  }
  return now.getTime() <= dueAt.getTime() + promptWindowMs;
};

/** Scheduled call that has left the hold stack (show a badge on the regular queue). */
export const isExpiredScheduledCall = (
  dueAt: Date | null,
  now: Date = new Date(),
  isScheduled: boolean = false,
): boolean =>
  isScheduled && dueAt != null && !isHeldScheduledCall(dueAt, now, isScheduled);

export const isScheduledCallDueForPrompt = (
  dueAt: Date | null,
  now: Date = new Date(),
  isScheduled: boolean = false,
): boolean => {
  if (!isHeldScheduledCall(dueAt, now, isScheduled) || dueAt == null) {
    return false;
  }
  return Math.abs(now.getTime() - dueAt.getTime()) <= promptWindowMs;
};

/** Scheduled-list selection is safe whenever no call/logging flow is active. */
export const canTakeScheduledCallNow = (
  workflowState: CallWorkflowState,
  isCurrentTask: boolean,
): boolean => !isCurrentTask && !isWorkflowCallInProgress(workflowState);

export const compareByPriorityThenDue = (
  a: CallWorkflowQueueItem,
  b: CallWorkflowQueueItem,
): number => {
  const aRank = a.ranking ?? Number.NEGATIVE_INFINITY;
  const bRank = b.ranking ?? Number.NEGATIVE_INFINITY;
  if (aRank !== bRank) {
    return bRank - aRank;
  }
  const aDue = a.dueAt?.getTime() ?? Number.POSITIVE_INFINITY;
  const bDue = b.dueAt?.getTime() ?? Number.POSITIVE_INFINITY;
  if (aDue !== bDue) {
    return aDue - bDue;
  }
  const created = a.createdAt.getTime() - b.createdAt.getTime();
  if (created !== 0) {
    return created;
  }
  return a.id - b.id;
};

const compareScheduledStack = (
  a: CallWorkflowQueueItem,
  b: CallWorkflowQueueItem,
): number => compareByPriorityThenDue(a, b);

/** Start of the 5-day active window: current 7am ET queue day minus 5 days. */
export const getAutodialerActiveQueueStart = (now: Date = new Date()): Date => {
  const { start } = getAutodialerQueueDayBounds(now);
  return DateTime.fromJSDate(start, { zone: COMPANY_TZ })
    .minus({ days: AUTODIALER_ACTIVE_QUEUE_LOOKBACK_DAYS })
    .toUTC()
    .toJSDate();
};

/** True when dueAt falls in the 5-day active window through next 7am ET. */
export const isAutodialerActiveQueueTask = (
  dueAt: Date | null | undefined,
  now: Date = new Date(),
): boolean => {
  if (dueAt == null) {
    return false;
  }
  return dueAt.getTime() >= getAutodialerActiveQueueStart(now).getTime();
};

/** Inclusive start through exclusive next-7am-ET end, formatted in Eastern. */
export const formatAutodialerActiveQueueDueRange = (
  now: Date = new Date(),
): string => {
  const start = getAutodialerActiveQueueStart(now);
  const { end } = getAutodialerQueueDayBounds(now);
  const startLabel = DateTime.fromJSDate(start, { zone: COMPANY_TZ }).toFormat(
    "MMM d, h:mm a",
  );
  const endLabel = DateTime.fromJSDate(end, { zone: COMPANY_TZ }).toFormat(
    "MMM d, h:mm a ZZZZ",
  );
  return `${startLabel} – ${endLabel}`;
};

export type AutodialerQueueSection = "active" | "backlog";

export const AUTODIALER_QUEUE_SECTION_LABELS: Record<
  AutodialerQueueSection,
  string
> = {
  active: "Last 5 days",
  backlog: "Backlog",
};

export const getAutodialerQueueSection = (
  dueAt: Date | null,
  now: Date = new Date(),
): AutodialerQueueSection =>
  isAutodialerActiveQueueTask(dueAt, now) ? "active" : "backlog";

export type AutodialerQueueSectionRow<T extends { dueAt: Date | null }> =
  | { kind: "header"; section: AutodialerQueueSection; label: string }
  | { kind: "task"; task: T; index: number };

/** Insert Active / Backlog headers wherever the visible queue changes section. */
export const getAutodialerQueueSectionRows = <T extends { dueAt: Date | null }>(
  tasks: T[],
  now: Date = new Date(),
  labels: Record<
    AutodialerQueueSection,
    string
  > = AUTODIALER_QUEUE_SECTION_LABELS,
): AutodialerQueueSectionRow<T>[] => {
  const rows: AutodialerQueueSectionRow<T>[] = [];
  let lastSection: AutodialerQueueSection | null = null;

  tasks.forEach((task, index) => {
    const section = getAutodialerQueueSection(task.dueAt, now);
    if (section !== lastSection) {
      rows.push({
        kind: "header",
        section,
        label: labels[section],
      });
      lastSection = section;
    }
    rows.push({ kind: "task", task, index });
  });

  return rows;
};

const getDueAtQueueDayStartMs = (dueAt: Date): number =>
  getAutodialerQueueDayBounds(dueAt).start.getTime();

/**
 * Regular-queue order: 5-day active window first, stack-ranked by the
 * priority table (no per-day grouping). Backlog (older than 5 days) is
 * grouped by rank, then by 7am–7am ET day (newest first). Tasks with no
 * dueAt stay in backlog at the end of their rank bucket.
 */
export const compareAutodialerQueueItems = (
  a: CallWorkflowQueueItem,
  b: CallWorkflowQueueItem,
  now: Date = new Date(),
): number => {
  const aActive = isAutodialerActiveQueueTask(a.dueAt, now);
  const bActive = isAutodialerActiveQueueTask(b.dueAt, now);
  if (aActive !== bActive) {
    return aActive ? -1 : 1;
  }

  if (aActive) {
    return compareByPriorityThenDue(a, b);
  }

  const aRank = a.ranking ?? Number.NEGATIVE_INFINITY;
  const bRank = b.ranking ?? Number.NEGATIVE_INFINITY;
  if (aRank !== bRank) {
    return bRank - aRank;
  }

  const aDated = a.dueAt != null;
  const bDated = b.dueAt != null;
  if (aDated !== bDated) {
    return aDated ? -1 : 1;
  }

  if (a.dueAt != null && b.dueAt != null) {
    const aDay = getDueAtQueueDayStartMs(a.dueAt);
    const bDay = getDueAtQueueDayStartMs(b.dueAt);
    if (aDay !== bDay) {
      return bDay - aDay;
    }
  }

  return compareByPriorityThenDue(a, b);
};

export const splitWorkflowQueue = (
  items: CallWorkflowQueueItem[],
  now: Date = new Date(),
): {
  queue: CallWorkflowQueueItem[];
  scheduledStack: CallWorkflowQueueItem[];
} => {
  const queue: CallWorkflowQueueItem[] = [];
  const scheduledStack: CallWorkflowQueueItem[] = [];

  for (const item of items) {
    if (isHeldScheduledCall(item.dueAt, now, item.isScheduled)) {
      scheduledStack.push(item);
    } else {
      queue.push(item);
    }
  }

  scheduledStack.sort(compareScheduledStack);
  queue.sort((a, b) => compareAutodialerQueueItems(a, b, now));
  return { queue, scheduledStack };
};

const connectTasksByGroupedIds = (
  items: CallWorkflowQueueItem[],
): CallWorkflowQueueItem[][] => {
  const parent = new Map<number, number>();
  const find = (id: number): number => {
    let current = id;
    while (parent.get(current) !== current) {
      const next = parent.get(current);
      if (next == null) {
        break;
      }
      current = next;
    }
    return current;
  };
  const union = (left: number, right: number): void => {
    const rootLeft = find(left);
    const rootRight = find(right);
    if (rootLeft !== rootRight) {
      parent.set(rootRight, rootLeft);
    }
  };

  for (const item of items) {
    parent.set(item.id, item.id);
  }
  const knownIds = new Set(items.map((item) => item.id));
  for (const item of items) {
    for (const otherId of item.groupedTaskIds ?? []) {
      if (knownIds.has(otherId)) {
        union(item.id, otherId);
      }
    }
  }

  const groups = new Map<number, CallWorkflowQueueItem[]>();
  for (const item of items) {
    const root = find(item.id);
    const group = groups.get(root) ?? [];
    group.push(item);
    groups.set(root, group);
  }
  return [...groups.values()];
};

export const findDueScheduledCall = (
  scheduledStack: CallWorkflowQueueItem[],
  skippedSet: Set<number>,
  {
    excludeTaskId = null,
    now = new Date(),
  }: { excludeTaskId?: number | null; now?: Date } = {},
): CallWorkflowQueueItem | null => {
  for (const task of scheduledStack) {
    if (skippedSet.has(task.id) || task.id === excludeTaskId) {
      continue;
    }
    if (isScheduledCallDueForPrompt(task.dueAt, now, task.isScheduled)) {
      return task;
    }
  }
  return null;
};

/**
 * Re-split queue and scheduledStack based on current time. A household stays
 * on the scheduled stack while any member is still a held scheduled callback.
 */
export const resplitWorkflowQueueClientSide = (
  queue: CallWorkflowQueueItem[],
  scheduledStack: CallWorkflowQueueItem[],
  now: Date = new Date(),
): {
  queue: CallWorkflowQueueItem[];
  scheduledStack: CallWorkflowQueueItem[];
} => {
  const nextQueue: CallWorkflowQueueItem[] = [];
  const nextScheduledStack: CallWorkflowQueueItem[] = [];

  for (const group of connectTasksByGroupedIds([...queue, ...scheduledStack])) {
    if (
      group.some((item) =>
        isHeldScheduledCall(item.dueAt, now, item.isScheduled),
      )
    ) {
      nextScheduledStack.push(...group);
    } else {
      nextQueue.push(...group);
    }
  }

  nextScheduledStack.sort(compareScheduledStack);
  nextQueue.sort((a, b) => compareAutodialerQueueItems(a, b, now));

  return { queue: nextQueue, scheduledStack: nextScheduledStack };
};

export const getSkippedTaskIdsFromQueue = (
  queue: Pick<CallWorkflowQueueItem, "id" | "skippedAt">[],
): number[] =>
  queue.filter((task) => task.skippedAt !== null).map((task) => task.id);

export const getCallableQueueTasks = (
  queue: CallWorkflowQueueItem[],
  skippedSet: Set<number>,
  completedTaskIds?: Set<number>,
): CallWorkflowQueueItem[] =>
  queue.filter(
    (task) =>
      !skippedSet.has(task.id) && !(completedTaskIds?.has(task.id) ?? false),
  );

/**
 * Session queue progress: position in the combined regular + scheduled
 * lists (including skipped and completed rows still present). Total is
 * those two lists together, not remaining-callable only.
 */
export const getWorkflowSessionQueueProgress = ({
  queue,
  scheduledStack = [],
  activeTaskId,
}: {
  queue: Pick<CallWorkflowQueueItem, "id">[];
  scheduledStack?: Pick<CallWorkflowQueueItem, "id">[];
  activeTaskId: number | null;
}): { taskNumber: number | null; total: number } => {
  const ordered = [...queue, ...scheduledStack];
  const total = ordered.length;
  if (activeTaskId == null) {
    return { taskNumber: null, total };
  }

  const idx = ordered.findIndex((task) => task.id === activeTaskId);
  if (idx < 0) {
    return { taskNumber: null, total };
  }

  return { taskNumber: idx + 1, total };
};

export const getWorkflowSessionQueueTaskNumber = (
  taskId: number,
  queue: Pick<CallWorkflowQueueItem, "id">[],
  scheduledStack: Pick<CallWorkflowQueueItem, "id">[],
): number | null => {
  const idx = [...queue, ...scheduledStack].findIndex(
    (task) => task.id === taskId,
  );
  return idx >= 0 ? idx + 1 : null;
};

export const findQueueTaskIndex = (
  queue: CallWorkflowQueueItem[],
  taskId: number | null,
): number | null => {
  if (taskId == null) {
    return null;
  }

  const idx = queue.findIndex((task) => task.id === taskId);
  return idx >= 0 ? idx : null;
};

/** Resolve a saved regular-queue resume target if it is still callable. */
export const findCallableQueueTaskById = (
  queue: CallWorkflowQueueItem[],
  taskId: number | null,
  skippedSet: Set<number>,
  completedTaskIds: Set<number>,
): CallWorkflowQueueItem | null => {
  if (taskId == null) {
    return null;
  }

  return (
    queue.find(
      (task) =>
        task.id === taskId &&
        !skippedSet.has(task.id) &&
        !completedTaskIds.has(task.id),
    ) ?? null
  );
};

/** First non-skipped task strictly after `afterTaskId` (or from the start). */
export const findNextNonSkippedQueueTask = (
  queue: CallWorkflowQueueItem[],
  skippedSet: Set<number>,
  afterTaskId: number | null,
  afterQueuePosition: number | null = null,
  completedTaskIds?: Set<number>,
): CallWorkflowQueueItem | null => {
  let startIdx = 0;
  if (afterTaskId != null) {
    const currentIdx = findQueueTaskIndex(queue, afterTaskId);
    if (currentIdx !== null) {
      startIdx = currentIdx + 1;
    } else if (
      afterQueuePosition !== null &&
      afterQueuePosition >= 0 &&
      afterQueuePosition < queue.length
    ) {
      startIdx = afterQueuePosition;
    }
  }

  for (let i = startIdx; i < queue.length; i++) {
    const task = queue[i];
    if (
      !skippedSet.has(task.id) &&
      !(completedTaskIds?.has(task.id) ?? false)
    ) {
      return task;
    }
  }

  // If we started searching after a task but found nothing, check from the beginning
  // (handles case where new higher-priority tasks were inserted before the current position)
  if (startIdx > 0) {
    for (let i = 0; i < startIdx; i++) {
      const task = queue[i];
      if (
        task.id !== afterTaskId &&
        !skippedSet.has(task.id) &&
        !(completedTaskIds?.has(task.id) ?? false)
      ) {
        return task;
      }
    }
  }

  return null;
};

/**
 * Queue cursor for a newly started workflow: first non-skipped task.
 */
export const getInitialWorkflowQueueCursor = (
  queue: CallWorkflowQueueItem[],
): { queuePosition: number | null; currentTaskId: number | null } => {
  const skippedSet = new Set(getSkippedTaskIdsFromQueue(queue));
  const firstTask = findNextNonSkippedQueueTask(queue, skippedSet, null);

  return {
    queuePosition: findQueueTaskIndex(queue, firstTask?.id ?? null),
    currentTaskId: firstTask?.id ?? null,
  };
};

export const reconcileQueuePosition = (
  queue: CallWorkflowQueueItem[],
  currentTaskId: number | null,
  queuePosition: number | null,
): number | null => {
  const taskIndex = findQueueTaskIndex(queue, currentTaskId);
  if (taskIndex !== null) {
    return taskIndex;
  }

  if (
    queuePosition !== null &&
    queuePosition >= 0 &&
    queuePosition < queue.length
  ) {
    return queuePosition;
  }

  return null;
};

const STUDENT_APP_PATH_PATTERN = /^\/students\/(\d+)(?:\/|$)/;

/** Student table id from an app path like `/students/123/messages`. */
export const getStudentIdFromAppPath = (path: string): number | null => {
  const pathname = path.split(/[?#]/)[0] ?? path;
  const match = pathname.match(STUDENT_APP_PATH_PATTERN);
  if (!match?.[1]) {
    return null;
  }

  const studentId = Number.parseInt(match[1], 10);
  return Number.isNaN(studentId) ? null : studentId;
};

/** True when both paths stay under the same `/students/{id}` route. */
export const isSameStudentProfileNavigation = (
  fromPath: string,
  toPath: string,
): boolean => {
  const fromStudentId = getStudentIdFromAppPath(fromPath);
  const toStudentId = getStudentIdFromAppPath(toPath);
  return (
    fromStudentId !== null &&
    toStudentId !== null &&
    fromStudentId === toStudentId
  );
};

/** Same-student tab changes, or a redirect onto the current/pending task's profile. */
export const isAllowedWorkflowNavigation = (
  fromPath: string,
  toPath: string,
  allowedStudentId: number | null,
): boolean => {
  if (isSameStudentProfileNavigation(fromPath, toPath)) {
    return true;
  }
  if (allowedStudentId == null) {
    return false;
  }
  return getStudentIdFromAppPath(toPath) === allowedStudentId;
};

/** Related-client profile path, keeping the current student-profile suffix. */
export const getRelatedClientProfilePath = (
  relatedStudentId: number,
  currentPath: string,
): string => {
  const pathname = currentPath.split(/[?#]/)[0] ?? currentPath;
  const match = pathname.match(/^\/students\/\d+(\/.*)?$/);
  const suffix = match?.[1] && match[1] !== "/" ? match[1] : "";
  return `/students/${relatedStudentId}${suffix}`;
};

/**
 * Arm the history trap once the CRM tab is on a student profile (or when
 * there is no task student to wait for). Do not arm on `/tasks`: that
 * `pushState` races `router.push` onto the first student.
 *
 * Stay armed across task-to-task profile switches. Toggling the trap off
 * while still on the previous student runs the cleanup `history.back()`
 * and can cancel the pending redirect.
 */
export const shouldArmWorkflowNavigationTrap = ({
  isWorkflowActive,
  currentPath,
  allowedStudentId,
}: {
  isWorkflowActive: boolean;
  currentPath: string;
  allowedStudentId: number | null;
}): boolean => {
  if (!isWorkflowActive) {
    return false;
  }
  if (allowedStudentId == null) {
    return true;
  }
  return getStudentIdFromAppPath(currentPath) != null;
};

export const getWorkflowQueueItemStudentId = (
  queue: Pick<CallWorkflowQueueItem, "id" | "studentId">[],
  taskId: number,
): number | null => {
  const item = queue.find((task) => task.id === taskId);
  return item?.studentId ?? null;
};

/**
 * Student the autodialer is allowed to open without the stop-workflow guard:
 * an in-flight task redirect, otherwise the current queue task's student.
 */
export const getWorkflowAllowedNavigationStudentId = ({
  pendingStudentId,
  currentTaskId,
  queue,
  scheduledStack,
}: {
  pendingStudentId: number | null | undefined;
  currentTaskId: number | null;
  queue: Pick<CallWorkflowQueueItem, "id" | "studentId">[];
  scheduledStack: Pick<CallWorkflowQueueItem, "id" | "studentId">[];
}): number | null => {
  if (pendingStudentId != null) {
    return pendingStudentId;
  }
  if (currentTaskId == null) {
    return null;
  }
  return getWorkflowQueueItemStudentId(
    [...queue, ...scheduledStack],
    currentTaskId,
  );
};

export const getWorkflowTaskStudentMessagesPath = (studentId: number): string =>
  `/students/${studentId}/messages`;

/**
 * True while autodialer is opening a different student than the one this
 * profile layout has actually rendered. Pathname can update before the RSC
 * commits (or stay stale after it does) — gate on rendered data instead.
 */
export const isAutodialerOpeningRenderedStudent = ({
  sessionId,
  pendingStudentId,
  renderedStudentId,
}: {
  sessionId: string | null;
  pendingStudentId: number | null | undefined;
  renderedStudentId: number;
}): boolean =>
  sessionId != null &&
  pendingStudentId != null &&
  pendingStudentId !== renderedStudentId;

export type AutodialerStuckTransitionRecovery = "refresh" | "replace" | "clear";

/**
 * How to unstick the "Opening…" overlay when the next student's profile
 * never replaces this layout.
 *
 * Soft retries: pathname matching the pending student means the App Router
 * updated the URL but the RSC didn't commit — refresh. Otherwise the
 * navigation never landed — replace.
 *
 * After `maxRetries` soft attempts, drop the overlay instead of
 * `location.assign`. A full load would trip the autodialer leave-page
 * warning and tear down the in-memory session.
 */
export const getAutodialerStuckTransitionRecovery = ({
  sessionId,
  pendingStudentId,
  renderedStudentId,
  pathStudentId,
  retries,
  maxRetries = WORKFLOW_PROFILE_TRANSITION_MAX_RETRIES,
}: {
  sessionId: string | null;
  pendingStudentId: number | null | undefined;
  renderedStudentId: number;
  pathStudentId: number | null;
  retries?: number;
  maxRetries?: number;
}): AutodialerStuckTransitionRecovery | null => {
  if (
    !isAutodialerOpeningRenderedStudent({
      sessionId,
      pendingStudentId,
      renderedStudentId,
    }) ||
    pendingStudentId == null
  ) {
    return null;
  }
  if (retries != null && retries > maxRetries) {
    return "clear";
  }
  if (pathStudentId === pendingStudentId) {
    return "refresh";
  }
  return "replace";
};

/**
 * After a silent Redis hydrate (refresh / new CRM tab), keep this tab on
 * whatever page it already has open. Ownership claiming and queue polls
 * must not re-open the next-student overlay for the restored task — that
 * overlay can stick through a refresh.
 *
 * Compare against Redis `currentTaskId`, not the queue-position fallback:
 * a poll that drops a just-logged task would otherwise look like a new
 * active task and fire navigation. Release once `currentTaskId` changes
 * (skip / next).
 */
export const shouldHoldSilentRestoreNavigation = ({
  suppressSilentRestoreNavigation,
  restoredTaskId,
  currentTaskId,
}: {
  suppressSilentRestoreNavigation: boolean;
  restoredTaskId: number | null;
  currentTaskId: number | null;
}): boolean =>
  suppressSilentRestoreNavigation && restoredTaskId === currentTaskId;

export const resolveActiveWorkflowTaskId = ({
  currentTaskId,
  queue,
  queuePosition,
}: {
  currentTaskId: number | null;
  queue: CallWorkflowQueueItem[];
  queuePosition: number | null;
}): number | null => {
  if (currentTaskId != null) {
    return currentTaskId;
  }

  if (queuePosition !== null && queue[queuePosition]) {
    return queue[queuePosition].id;
  }

  return null;
};

/**
 * Which task a restored autodialer session should land on.
 *
 * Page navigation (refresh, new CRM tab, sibling profile, referral) must not
 * change the current call. Only skip / next in the autodialer should. Silent
 * hydrate therefore keeps Redis `currentTaskId` even when that row has dropped
 * from the queue (logged but not advanced).
 *
 * Explicit Start Workflow may fall forward when Redis already points at a
 * completed task that is gone from the fresh queue.
 */
export const resolveRestoredWorkflowCurrentTaskId = ({
  restoredCurrentTaskId,
  queue,
  scheduledStack,
  skippedTaskIds,
  queuePosition,
  preserveRestoredTask = false,
}: {
  restoredCurrentTaskId: number | null;
  queue: CallWorkflowQueueItem[];
  scheduledStack: CallWorkflowQueueItem[];
  skippedTaskIds: number[];
  queuePosition: number | null;
  preserveRestoredTask?: boolean;
}): number | null => {
  const skippedSet = new Set(skippedTaskIds);
  const allItems = [...queue, ...scheduledStack];

  // Refresh and new CRM tabs: never infer "done — skip ahead" and never
  // retarget to the tab's URL student.
  if (preserveRestoredTask) {
    return restoredCurrentTaskId;
  }

  if (
    restoredCurrentTaskId != null &&
    allItems.some((task) => task.id === restoredCurrentTaskId)
  ) {
    return restoredCurrentTaskId;
  }

  return (
    findNextNonSkippedQueueTask(
      queue,
      skippedSet,
      restoredCurrentTaskId,
      queuePosition,
    )?.id ?? null
  );
};

/** Hide call cards only when no task is active and nothing is in-call / logging. */
export const shouldShowWorkflowQueueComplete = ({
  queue,
  scheduledStack = [],
  skippedSet,
  completedTaskIds = new Set<number>(),
  activeTaskId,
  state,
}: {
  queue: CallWorkflowQueueItem[];
  scheduledStack?: CallWorkflowQueueItem[];
  skippedSet: Set<number>;
  completedTaskIds?: Set<number>;
  activeTaskId: number | null;
  state: CallWorkflowState;
}): boolean => {
  if (isWorkflowCallInProgress(state)) {
    return false;
  }

  const hasActiveCallableTask =
    activeTaskId != null &&
    !skippedSet.has(activeTaskId) &&
    !completedTaskIds.has(activeTaskId);
  if (hasActiveCallableTask) {
    return false;
  }

  const hasCallableTask = queue.some(
    (task) => !skippedSet.has(task.id) && !completedTaskIds.has(task.id),
  );
  if (hasCallableTask) {
    return false;
  }

  const hasHeldScheduled = scheduledStack.some(
    (task) => !skippedSet.has(task.id),
  );
  return !hasHeldScheduled;
};

export type WorkflowSessionSummaryState = {
  completedCount: number;
  convertedCount: number;
  /** Tasks explicitly skipped by the user during this session. */
  sessionSkippedCount: number;
  sessionStartedAt: Date | null;
  startedAtIso?: string;
  sessionBaselineCompletedCount: number;
  /** Queue tasks completed this session. Defaults to session dials when omitted. */
  tasksCompletedCount?: number;
};

/** Restore session skip count from Redis, with a legacy fallback when unset. */
export const resolveSessionSkippedCountFromRedis = (
  session: Pick<
    WorkflowRedisSession,
    "sessionSkippedCount" | "skippedCount" | "sessionBaselineSkippedCount"
  >,
): number => {
  if (session.sessionSkippedCount != null) {
    return session.sessionSkippedCount;
  }

  const baseline = session.sessionBaselineSkippedCount ?? 0;
  return Math.max(0, session.skippedCount - baseline);
};

export const buildWorkflowSessionSummaryStats = (
  snapshot: WorkflowSessionSummaryState,
): WorkflowSessionSummaryStats => getWorkflowSessionSummaryStats(snapshot);

export const getWorkflowSessionSummaryStats = ({
  completedCount,
  convertedCount,
  sessionSkippedCount,
  sessionStartedAt,
  startedAtIso,
  sessionBaselineCompletedCount = 0,
  tasksCompletedCount,
}: {
  completedCount: number;
  convertedCount: number;
  sessionSkippedCount: number;
  sessionStartedAt: Date | null;
  startedAtIso?: string;
  sessionBaselineCompletedCount?: number;
  tasksCompletedCount?: number;
}): WorkflowSessionSummaryStats => {
  const startedAtMs =
    startedAtIso != null
      ? new Date(startedAtIso).getTime()
      : sessionStartedAt?.getTime();
  const durationSeconds =
    startedAtMs != null
      ? Math.max(0, Math.floor((Date.now() - startedAtMs) / 1000))
      : 0;
  const callsMade = Math.max(0, completedCount - sessionBaselineCompletedCount);

  return {
    callsMade,
    tasksCompleted: tasksCompletedCount ?? callsMade,
    converted: convertedCount,
    skipped: sessionSkippedCount,
    durationSeconds,
  };
};

export const workflowSessionSummaryStatsToAnalyticsContext = (
  stats: WorkflowSessionSummaryStats,
): Record<string, unknown> => ({
  callsMade: stats.callsMade,
  tasksCompleted: stats.tasksCompleted,
  converted: stats.converted,
  skipped: stats.skipped,
  durationSeconds: stats.durationSeconds,
});
